import React, { useCallback, useEffect, useMemo, useState } from "react";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ONE_DAY_MS } from "@/components/roster/rosterUtils";
import { DepthChart } from "@/components/roster/DepthChart";
import { RosterSettingsDialog } from "@/components/roster/RosterSettingsDialog";
import {
  RosterActions, RosterBanners, RosterStats, RosterFilters, GroupEmailBar, RosterTable,
} from "@/components/roster/RosterSections";
import { emailListsFor, allEmails, mailGroup, broadcastProgram } from "@/components/roster/rosterEmail";

const errDetail = (e, fallback) => {
  const detail = e.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

function useRosterData() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roster");
      setData(data);
    } catch (e) {
      toast.error(errDetail(e, "Could not load roster"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/roster/sync");
      toast.success(`Roster synced — ${res.data.count} player${res.data.count === 1 ? "" : "s"} loaded`);
      await load();
    } catch (e) {
      toast.error(errDetail(e, "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };
  return { data, loading, syncing, load, sync };
}

function filterPlayers(players, q, posFilter) {
  const term = q.trim().toLowerCase();
  return (players || []).filter((p) => {
    if (posFilter && p.position !== posFilter) return false;
    if (!term) return true;
    const hay = `${p.last_name} ${p.first_name} ${p.position} ${p.player_email} ${p.guardian_first_name} ${p.guardian_last_name} ${p.guardian_email}`.toLowerCase();
    return hay.includes(term);
  });
}

export default function Roster() {
  const { user } = useAuth();
  const { data, loading, syncing, load, sync } = useRosterData();
  const [showSettings, setShowSettings] = useState(false);
  const [q, setQ] = useState("");
  const [posFilter, setPosFilter] = useState("");
  const [view, setView] = useState("list");

  const players = data?.players;
  const filtered = useMemo(() => filterPlayers(players, q, posFilter), [players, q, posFilter]);
  const positionsAll = useMemo(
    () => [...(data?.positions?.standard || []), ...(data?.positions?.custom || [])], [data]);
  const stats = useMemo(() => ({
    total: players?.length || 0,
    ad: (players || []).filter(p => p.ad_cleared).length,
    coach: (players || []).filter(p => p.coach_cleared).length,
  }), [players]);
  const emailLists = useMemo(() => emailListsFor(filtered), [filtered]);
  const broadcastList = useMemo(() => allEmails(players || []), [players]);
  const lastSynced = data?.config?.last_synced_at;
  const staleForReminder = !lastSynced || (Date.now() - new Date(lastSynced).getTime()) > ONE_DAY_MS;

  const openMailto = (list, kind) => mailGroup(list, kind, posFilter, user?.program_name);
  const openBroadcast = () => broadcastProgram(broadcastList, user?.program_name);

  const hasSheetUrl = !!data?.config?.sheet_url;
  const canManage = !!data?.can_manage;

  if (loading && !data) {
    return <div data-testid="roster-loading" className="p-8 text-slate-400">Loading roster…</div>;
  }

  return (
    <div data-testid="roster-page">
      <TopBar title="Team Roster" subtitle="Synced from your shared Google Sheet"
        actions={<RosterActions canManage={canManage} hasSheetUrl={hasSheetUrl} broadcastList={broadcastList}
          openBroadcast={openBroadcast} data={data} posFilter={posFilter} view={view}
          sync={sync} syncing={syncing} setShowSettings={setShowSettings} />}
      />
      <div className="p-8 max-w-7xl space-y-6">
        <RosterBanners hasSheetUrl={hasSheetUrl} canManage={canManage} setShowSettings={setShowSettings}
          data={data} staleForReminder={staleForReminder} sync={sync} syncing={syncing} />
        {hasSheetUrl && <RosterStats stats={stats} data={data} />}
        {hasSheetUrl && players?.length > 0 && (
          <>
            <RosterFilters view={view} setView={setView} q={q} setQ={setQ}
              posFilter={posFilter} setPosFilter={setPosFilter} positionsAll={positionsAll} />
            <GroupEmailBar posFilter={posFilter} emailLists={emailLists} openMailto={openMailto} />
          </>
        )}
        {hasSheetUrl && (
          <div className="bg-surface border border-surface-3 overflow-x-auto" data-testid="roster-table-wrap">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">
                {players?.length === 0
                  ? "No players synced yet. Fill in the sheet, then hit Sync From Sheet."
                  : "No players match this filter."}
              </div>
            ) : view === "depth" ? (
              <DepthChart players={filtered} positionsOrder={positionsAll}
                coaches={data?.position_coaches_resolved || {}} />
            ) : (
              <RosterTable filtered={filtered} />
            )}
          </div>
        )}
      </div>
      {showSettings && (
        <RosterSettingsDialog data={data} onClose={() => setShowSettings(false)} onSaved={load} />
      )}
    </div>
  );
}
